/**
 * iter-338w — "Suggested Next Action" banner.
 *
 * Displayed at the top of the Client and Vendor dashboards. Fetches
 * `/api/ai-chat/next-action` and shows ONE personalised, dismissible card
 * with a CTA that deep-links to the exact page.
 *
 * Dismissal is session-local (sessionStorage) — re-shows on next login.
 */
import { useEffect, useState } from 'react';
import axios from 'axios';
import { X, Sparkles, ArrowRight, AlertTriangle, Info, CheckCircle2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SEVERITY = {
  action: {
    bg: 'bg-gradient-to-r from-amber-50 via-amber-100 to-orange-50',
    border: 'border-amber-300',
    accent: 'text-amber-900',
    icon: Sparkles,
    ring: 'text-amber-600',
    cta: 'bg-amber-600 hover:bg-amber-700 text-white',
  },
  warn: {
    bg: 'bg-gradient-to-r from-red-50 via-rose-50 to-red-100',
    border: 'border-red-300',
    accent: 'text-red-900',
    icon: AlertTriangle,
    ring: 'text-red-600',
    cta: 'bg-red-600 hover:bg-red-700 text-white',
  },
  info: {
    bg: 'bg-gradient-to-r from-blue-50 via-sky-50 to-indigo-50',
    border: 'border-blue-200',
    accent: 'text-blue-900',
    icon: Info,
    ring: 'text-blue-500',
    cta: 'bg-blue-600 hover:bg-blue-700 text-white',
  },
  ok: {
    bg: 'bg-gradient-to-r from-emerald-50 via-green-50 to-emerald-100',
    border: 'border-emerald-200',
    accent: 'text-emerald-900',
    icon: CheckCircle2,
    ring: 'text-emerald-500',
    cta: 'bg-emerald-600 hover:bg-emerald-700 text-white',
  },
};

export default function NextActionBanner() {
  const [action, setAction] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;
    // Session-local dismissal so it re-shows on next login
    if (sessionStorage.getItem('next_action_dismissed') === '1') {
      setDismissed(true);
      return;
    }
    axios
      .get(`${API}/ai-chat/next-action`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setAction(r.data))
      .catch(() => setAction(null));
  }, []);

  if (dismissed || !action || !action.ok) return null;

  // Map backend severity → theme
  let sev = SEVERITY[action.severity] || SEVERITY.info;
  if (action.kind && action.kind.endsWith('_ok')) sev = SEVERITY.ok;
  const Icon = sev.icon;

  return (
    <div
      className={`rounded-xl border ${sev.bg} ${sev.border} p-4 mb-4 flex items-start gap-3 shadow-sm`}
      data-testid="next-action-banner"
    >
      <div className={`p-1.5 rounded-lg bg-white/60 ${sev.ring} shrink-0`}>
        <Icon size={20} strokeWidth={2} />
      </div>
      <div className="flex-1 min-w-0">
        <div className={`font-semibold text-sm ${sev.accent}`} data-testid="next-action-title">
          {action.title}
        </div>
        <div className="text-[13px] text-stone-700 mt-0.5" data-testid="next-action-body">
          {action.body}
        </div>
      </div>
      {action.cta_href && (
        <a
          href={action.cta_href}
          className={`inline-flex items-center gap-1 text-xs font-semibold px-3 py-1.5 rounded-md ${sev.cta} shrink-0 transition-colors`}
          data-testid="next-action-cta"
        >
          {action.cta_label}
          <ArrowRight size={13} />
        </a>
      )}
      <button
        type="button"
        onClick={() => {
          setDismissed(true);
          sessionStorage.setItem('next_action_dismissed', '1');
        }}
        className="text-stone-400 hover:text-stone-700 shrink-0 p-1"
        aria-label="Dismiss"
        data-testid="next-action-dismiss"
      >
        <X size={14} />
      </button>
    </div>
  );
}
